import { useCallback, useState } from 'react'

import { useParams } from 'react-router'
import { LoadingDots } from 'src/components/LoadingDots/LoadingDots'
import { httpStatus } from 'src/constants/statusCodes'
import { useSendVideoCallStartedNotification } from 'src/features/explanation/api/sendVideoCallStartedNotification'
import { NotFound } from 'src/features/misc/components/NotFound/NotFound'
import { PatientVideoCallSignIn } from 'src/features/misc/components/PatientVideoCallSignIn/PatientVideoCallSignIn'
import { useFeatureFlags } from 'src/features/trial/api/getFeatureFlags'
import { VideoCallRoom } from 'src/features/videoCall/components'
import { ExplanationPatientForPatientTrialPatientVideoCallScreenDocument } from 'src/lib/gql-client'
import { useGqlQuerySWRNoSuspense } from 'src/lib/gql-client/request'

import { useTrialPatientVideoCallToken } from '../../api/setupTrialPatientVideoCallToken'

type Param = {
  trialUid: string
  trialPatientUid: string
  visitVideoCallUid: string
}

export const PatientTrialPatientVideoCallScreen: React.FC = () => {
  const { trialUid, trialPatientUid } = useParams<Param>()

  const { data, setupJwtToken } = useTrialPatientVideoCallToken()

  const [isUnauthorized, setIsUnauthorized] = useState(false)

  const [hasUnexpectedError, setHasUnexpectedError] = useState(false)

  if (!trialUid || !trialPatientUid) {
    throw new Error('trialUid or trialPatientUid is not provided')
  }

  const { request: sendVideoCallStartedNotification } =
    useSendVideoCallStartedNotification()

  const { data: featureFlags } = useFeatureFlags({
    trialUid,
  })

  // 患者認証チェック
  // 本来、eConsentに依存させたくない場所ではあるが、participantName取得のため許容
  const { isLoading, mutate } = useGqlQuerySWRNoSuspense(
    ExplanationPatientForPatientTrialPatientVideoCallScreenDocument,
    !!data?.token
      ? null
      : {
          input: {
            trialPatientUid,
          },
        },
    {
      onSuccess: async () => {
        setIsUnauthorized(false)
        setHasUnexpectedError(false)
        await setupJwtToken(trialPatientUid)
      },
      onError: error => {
        if (error.status === httpStatus.Unauthorized) {
          setIsUnauthorized(true)
          return
        }
        setHasUnexpectedError(true)
      },
    },
  )

  const sendNotification = useCallback(() => {
    sendVideoCallStartedNotification({
      trialPatientUid,
      scene: 'VideoCall',
      sendTo: 'Patient',
    })
  }, [trialPatientUid, sendVideoCallStartedNotification])

  if (isLoading || !featureFlags || !data) {
    return <LoadingDots />
  }

  if (isUnauthorized) {
    return (
      <PatientVideoCallSignIn
        trialUid={trialUid}
        trialPatientUid={trialPatientUid}
        onSignIn={mutate}
      />
    )
  }
  if (hasUnexpectedError) {
    return <NotFound />
  }

  return (
    <VideoCallRoom
      token={data.token}
      canShareScreen={false}
      sendNotification={sendNotification}
      videoCallUserUid={data.videoCallUserUid}
      roomUid={data.videoCallRoomUid}
      screenShareUserUid={data.screenShareUserUid}
      screenShareToken={data.screenShareToken}
      scene="EConsentVideoCall" // eSourceにいtrialPatientVideoCallを使う可能性はあるが、現状は"EConsentExplanation"のみ
      agoraCloudProxyMode={data.agoraCloudProxyMode}
    />
  )
}
